import React, { forwardRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import { useTranslation } from 'react-i18next';
import { InputRegexPattern } from '../../../models/Common';

interface FormInputPasswordProps {
  name: string;
  label: string;
  className: string;
  isRequired?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  helperText?: string;
  autoComplete?: string;
  placeholder?: string;
  fieldHasName?: boolean;
}

const defaultProps = {
  isRequired: false,
  minLength: 0,
  maxLength: 99,
  pattern: /^[a-zA-Z0-9_-]*$/i,
  helperText: undefined,
  autoComplete: 'off',
  placeholder: '',
  fieldHasName: false,
};

export const FormInputPassword = forwardRef<HTMLInputElement, FormInputPasswordProps>(
  (
    {
      autoComplete = 'off',
      name,
      placeholder,
      fieldHasName,
      label,
      className,
      isRequired,
      minLength,
      maxLength,
      pattern,
      helperText,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState(false);
    const { control } = useFormContext();
    const handleClickShowPassword = () => {
      setIsShowPassword(!isShowPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    const getPatternErrorMessage = (reg: RegExp) => {
      switch (true) {
        case reg === InputRegexPattern.OneNumberOneLetter: {
          return t('validation.oneNumberAndOneLetterAreRequired');
        }
        default:
          return t('validation.invalidValue');
      }
    };

    const validationRules = {
      required: {
        value: isRequired || defaultProps.isRequired,
        message: t('validation.thisFieldIsRequired'),
      },
      minLength: {
        value: minLength || defaultProps.minLength,
        message: `${t('validation.theNumberOfCharactersMustBeAtLeast')} ${minLength}`,
      },
      maxLength: {
        value: maxLength || defaultProps.maxLength,
        message: `${t('validation.theNumberOfCharactersMustBeNoMoreThan')} ${maxLength}`,
      },
      pattern: {
        value: pattern || defaultProps.pattern,
        message: getPatternErrorMessage(pattern || defaultProps.pattern),
      },
    };

    return (
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field: { onChange, value }, fieldState: { error, isDirty, isTouched } }) => (
          <TextField
            inputRef={ref}
            placeholder={placeholder}
            error={!!error}
            helperText={(error && error.message) || (helperText && helperText)}
            label={label}
            onChange={onChange}
            name={fieldHasName ? name : undefined}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            value={value || ''}
            className={`input input__text-field__${className}`}
            type={isShowPassword ? 'text' : 'password'}
            autoComplete={autoComplete}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className="toggle-password"
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {isShowPassword ? (
                      <VisibilityOffOutlined className="hide-password" />
                    ) : (
                      <VisibilityOutlined className="show-password" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: !!value || isFocused || !!placeholder || isDirty || isTouched }}
          />
        )}
      />
    );
  },
);

FormInputPassword.defaultProps = defaultProps;
